import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Instagram, Facebook, Linkedin, Youtube, Twitter, Send, MessageCircle } from "lucide-react";
import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { FOUNDATION } from "@/data/content";
import { toast } from "sonner";
import { useLocale } from "@/context/LocaleContext";
import { useSettings } from "@/context/SettingsContext";
import LocaleSwitcher from "@/components/common/LocaleSwitcher";
import { getImageUrl } from "@/lib/api";

const SOCIAL_ICONS = { instagram: Instagram, facebook: Facebook, linkedin: Linkedin, youtube: Youtube, twitter: Twitter, whatsapp: MessageCircle, tiktok: MessageCircle };

export default function Footer() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const { t } = useLocale();
  const { settings, customNavPages } = useSettings();

  const f = settings?.footer || {};
  const c = settings?.contact || {};
  const found = settings?.foundation || {};

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSending(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("Terima kasih! Anda telah berlangganan newsletter kami.");
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const socials = c.socials || {};

  return (
    <footer className="relative bg-[#0B1A3D] text-slate-200 overflow-hidden" data-testid="site-footer">
      <div className="pointer-events-none absolute -top-40 -right-40 h-96 w-96 rounded-full" style={{ background: "radial-gradient(circle, rgba(242,140,31,.18) 0%, transparent 70%)" }} />
      <div className="pointer-events-none absolute -bottom-40 -left-40 h-96 w-96 rounded-full" style={{ background: "radial-gradient(circle, rgba(27,58,142,.4) 0%, transparent 70%)" }} />

      <div className="container-sf relative py-16 md:py-20">
        {/* Newsletter row */}
        <div className="grid lg:grid-cols-[1.3fr,1fr] gap-10 pb-14 border-b border-white/10">
          <div>
            <div className="eyebrow text-[var(--brand-orange)] mb-3">{f.newsletter_eyebrow || t("footer.newsletter_eyebrow")}</div>
            <h3 className="text-3xl md:text-4xl font-bold text-white leading-tight max-w-xl">
              {f.newsletter_title || t("footer.newsletter_title")}
            </h3>
          </div>
          <form onSubmit={subscribe} className="flex flex-col justify-center gap-3" data-testid="newsletter-form">
            <div className="flex bg-white/10 border border-white/20 rounded-full p-1.5 backdrop-blur">
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder={t("footer.newsletter_placeholder")}
                data-testid="newsletter-email-input"
                className="flex-1 bg-transparent px-5 text-sm md:text-base text-white placeholder-slate-400 outline-none"
              />
              <button type="submit" disabled={sending} data-testid="newsletter-submit" className="btn-primary !py-2.5 !px-5 !text-sm shrink-0">
                <Send className="h-4 w-4" /> {sending ? "..." : t("footer.newsletter_button")}
              </button>
            </div>
            <p className="text-xs text-slate-400">{f.newsletter_note || t("footer.newsletter_note")}</p>
          </form>
        </div>

        {/* Main grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-10 pt-14">
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-xl bg-white flex items-center justify-center shrink-0">
                <img src={getImageUrl(found.logo_url || FOUNDATION.logo)} alt="Sauyunan" className="h-10 w-10 object-contain" />
              </div>
              <div>
                <div className="text-lg font-bold text-white tracking-tight">{found.short_name || FOUNDATION.shortName}</div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--brand-orange)]">Foundation</div>
              </div>
            </Link>
            <p className="mt-5 text-sm text-slate-300 leading-relaxed max-w-md">
              {f.description || t("footer.desc")}
            </p>
            <p className="mt-4 text-sm font-serif-display italic text-[var(--brand-orange)]">&ldquo;{found.tagline_sundanese || FOUNDATION.taglineSundanese}&rdquo;</p>
            <div className="mt-5 flex items-center gap-3 flex-wrap">
              {Object.entries(socials).filter(([, url]) => url).map(([key, url]) => {
                const Icon = SOCIAL_ICONS[key] || MessageCircle;
                return <SocialLink key={key} href={url} icon={Icon} label={key} />;
              })}
              <LocaleSwitcher variant="footer" />
            </div>
          </div>

          <FooterCol title={f.col_explore_title || t("footer.col_explore")} items={[
            { label: t("nav.about.foundation"), to: "/tentang" },
            { label: t("nav.programs"), to: "/program" },
            { label: t("nav.impact"), to: "/impact" },
            { label: t("nav.news"), to: "/berita" },
            { label: t("nav.gallery"), to: "/galeri" },
            { label: t("nav.team"), to: "/tim" },
          ]} />

          <FooterCol title={f.col_action_title || t("footer.col_action")} items={[
            { label: t("nav.donate"), to: "/donasi" },
            { label: t("nav.volunteer"), to: "/volunteer" },
            { label: t("nav.partners"), to: "/mitra" },
            { label: t("nav.publications"), to: "/publikasi" },
            { label: t("nav.contact"), to: "/kontak" },
            ...(customNavPages || []).slice(0, 3).map((p) => ({ label: p.nav_label || p.title, to: `/p/${p.slug}` })),
          ]} />

          <div>
            <div className="text-sm font-semibold text-white mb-4 uppercase tracking-wider">{f.col_contact_title || t("footer.col_contact")}</div>
            <ul className="space-y-3 text-sm text-slate-300">
              <li className="flex gap-3">
                <MapPin className="h-4 w-4 mt-0.5 text-[var(--brand-orange)] shrink-0" />
                <span>{c.address_line1 || FOUNDATION.contact.address}, {c.address_line2 || FOUNDATION.contact.addressLine2}, {c.address_line3 || FOUNDATION.contact.addressLine3}</span>
              </li>
              <li className="flex gap-3">
                <Phone className="h-4 w-4 mt-0.5 text-[var(--brand-orange)] shrink-0" />
                <div className="flex flex-col">{(c.phones || FOUNDATION.contact.phones).map((p) => <span key={p}>{p}</span>)}</div>
              </li>
              <li className="flex gap-3">
                <Mail className="h-4 w-4 mt-0.5 text-[var(--brand-orange)] shrink-0" />
                <a href={`mailto:${c.email || FOUNDATION.contact.email}`} className="hover:text-white">{c.email || FOUNDATION.contact.email}</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-slate-400">
          <div>© {new Date().getFullYear()} {found.legal_name || FOUNDATION.legalName}. All rights reserved. {f.copyright_extra || ""}</div>
          <div className="flex items-center gap-6">
            <Link to={f.privacy_url || "/privacy"} className="hover:text-white">{f.privacy_label || t("footer.privacy")}</Link>
            <Link to={f.terms_url || "/terms"} className="hover:text-white">{f.terms_label || t("footer.terms")}</Link>
            {(f.show_admin_link !== false) && <Link to="/admin/login" className="hover:text-white opacity-60">Admin</Link>}
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, items }) {
  return (
    <div>
      <div className="text-sm font-semibold text-white mb-4 uppercase tracking-wider">{title}</div>
      <ul className="space-y-2.5 text-sm text-slate-300">
        {items.map((i) => (
          <li key={`${i.label}-${i.to}`}><Link to={i.to} className="hover:text-white transition">{i.label}</Link></li>
        ))}
      </ul>
    </div>
  );
}

function SocialLink({ href, icon: Icon, label }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" aria-label={label} data-testid={`social-${label.toLowerCase()}`} className="h-10 w-10 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-[var(--brand-orange)] transition text-white">
      <Icon className="h-4 w-4" />
    </a>
  );
}
